import React, {
  useMemo,
  useState,
  useCallback, useRef, useEffect,
} from 'react';

import styled from 'styled-components';

import { useLocation } from 'react-router-dom';

import { LinksGroups } from './LinksGroups';
import { MenuCollapser } from './MenuCollapser';
import { renderGroupItem } from './LinksGroup';

import { constructLinksPath } from './constructLinksPath';

import {
  MENU_DEFAULT,
  MENU_COLLAPSED,
  MENU_HOVER,
} from './constants';
import {
  getCollapserHeight,
} from './style';

import type {
  SidebarProps,
} from './types';

export const StyledWrapper = styled.div<{
  isExample: boolean;
  isCollapsible: boolean;
  isCollapsed: boolean;
  isMenuCollapsed: boolean;
}>(({
  isExample,
  isCollapsible,
  isCollapsed,
  isMenuCollapsed,

  theme,
  theme: {
    colors,
    grid,
    indents,
    layout,
  },
}) => ({
  display: isCollapsed ? 'none' : 'block',
  boxSizing: 'border-box',
  position: isExample ? 'relative' : 'fixed',
  left: isExample ? undefined : 0,
  top: isExample ? undefined : 0,
  height: isExample ? 400 : '100%',
  width: isMenuCollapsed ? layout.SIDEBAR_WIDTH_COLLAPSED : layout.SIDEBAR_WIDTH,
  paddingTop: isCollapsible ? (layout.HEADER_HEIGHT + indents.INDENT_2) : indents.INDENT_2,
  paddingBottom: getCollapserHeight(theme),
  borderRightWidth: 1,
  borderRightStyle: 'solid',
  borderRightColor: colors.NEUTRAL_INACTIVE,
  backgroundColor: colors.NEUTRAL_WHITE,

  [`@media (min-width: ${grid.SCREEN_WIDTH_MD}px)`]: {
    display: 'block',
    paddingTop: layout.HEADER_HEIGHT + indents.INDENT_2,
  },
}));

const StyledHoverGroup = styled.div<{
  isExample: boolean;
}>(({
  theme,
  theme: {
    colors,
    indents,
    layout,
  },
}) => ({
  boxSizing: 'border-box',
  position: 'absolute',
  top: 0,
  left: '100%',
  height: '100%',
  width: layout.SIDEBAR_WIDTH,
  paddingTop: layout.HEADER_HEIGHT + indents.INDENT_2,
  paddingBottom: getCollapserHeight(theme),
  borderRightWidth: 1,
  borderRightStyle: 'solid',
  borderRightColor: colors.NEUTRAL_INACTIVE,
  backgroundColor: colors.NEUTRAL_WHITE,
}));

// https://github.com/styled-components/styled-components/issues/3135
export const StyledScrollable = styled.div`
  overflow: auto;
  max-height: 100%;
  overscroll-behavior: contain;

  &::-webkit-scrollbar {
    background-color: transparent;
    width: 16px;
    height: 16px;
  }

  &::-webkit-scrollbar-track {
    background-color: #fff;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(0,0,0,0.1);
    border-radius: 16px;
    border: 4px solid #fff;
  }

    &::-webkit-scrollbar-button {
      display: none;
    }

    &::-webkit-scrollbar-thumb:hover {
        background-color: rgba(0,0,0,0.2);
        border-radius: 16px;
        border: 4px solid #fff;
    }
`;

export const emptyFn = () => undefined;

const emptyObj = {};

export function Sidebar({
  linksGroups,
  counters = emptyObj,
  countersMaxNumber = null,
  showCountersInOpenedState = false,

  isExample = false,

  collapsible = true,
  collapsed = true,

  openedGroups = emptyObj,
  openedByDefault = false,

  menuCollapsible = true,
  menuCollapsed = false,

  closeMenu = emptyFn,
  setMenuCollapsed = emptyFn,
  onToggleGroup = emptyFn,
}: SidebarProps) {
  const innerRef = useRef<HTMLDivElement>(null);
  const outerRef = useRef<HTMLDivElement>(null);

  const location = useLocation();
  const [hoverGroupIndex, setHoverGroupIndex] = useState<number | null>(null);

  const linksPath = useMemo(
    () => constructLinksPath(linksGroups, location.pathname),
    [linksGroups, location.pathname],
  );

  const resetHoverGroupIndex = useCallback(() => {
    if (menuCollapsed) {
      setHoverGroupIndex(null);
    }
  }, [menuCollapsed]);

  const hoverGroup = menuCollapsed
    && hoverGroupIndex !== null
    && linksGroups[hoverGroupIndex];

  const handleScroll = (e: WheelEvent) => {
    const innerDiv = innerRef.current;

    if (!innerDiv) {
      // Меню находится в свернутом режиме.
      e.stopPropagation();
      e.preventDefault();

      return;
    }

    const { scrollTop, offsetHeight, scrollHeight } = innerDiv;

    // Проверяем, находится ли прокрутка в верхней или нижней позиции.
    const atTop = scrollTop === 0;
    const atBottom = scrollTop + offsetHeight === scrollHeight;

    if (atTop && e.deltaY < 0) {
      // Если прокрутка находится в верхней позиции и событие прокрутки идет вверх
      e.stopPropagation();
      e.preventDefault();
    } else if (atBottom && e.deltaY > 0) {
      // Если прокрутка находится в нижней позиции и событие прокрутки идет вниз
      e.stopPropagation();
      e.preventDefault();
    }
  };

  useEffect(() => {
    const element = outerRef.current;

    element?.addEventListener('wheel', handleScroll, { passive: false });

    return () => {
      element?.removeEventListener('wheel', handleScroll);
    };
  }, []);

  return (
    <StyledWrapper
      isExample={isExample}
      isCollapsible={collapsible}
      isCollapsed={collapsible && collapsed}
      isMenuCollapsed={menuCollapsed}
      onMouseLeave={resetHoverGroupIndex}
      ref={outerRef}
    >
      <StyledScrollable ref={innerRef}>
        <LinksGroups
          level={0}
          linksPath={linksPath || undefined}
          linksGroups={linksGroups}
          counters={counters}
          countersMaxNumber={countersMaxNumber}
          showCountersInOpenedState={showCountersInOpenedState}
          renderType={menuCollapsed ? MENU_COLLAPSED : MENU_DEFAULT}
          closeMenu={menuCollapsed ? resetHoverGroupIndex : closeMenu}
          hoverGroupIndex={hoverGroupIndex}
          openedGroups={openedGroups}
          openedByDefault={openedByDefault}
          setHoverGroupIndex={menuCollapsed ? setHoverGroupIndex : emptyFn}
          onToggleGroup={onToggleGroup}
        />
      </StyledScrollable>

      {
        menuCollapsible && (
          <MenuCollapser
            menuCollapsed={menuCollapsed}
            setMenuCollapsed={setMenuCollapsed}
          />
        )
      }

      {
        hoverGroup && (
          <StyledHoverGroup
            isExample={isExample}
          >
            <StyledScrollable>
              {
                renderGroupItem({
                  level: 0,
                  counters,
                  countersMaxNumber,
                  showCountersInOpenedState,
                  closeMenu: resetHoverGroupIndex,
                  index: hoverGroupIndex,
                  renderType: MENU_HOVER,
                  openedGroups,
                }, hoverGroup, linksPath)
              }
            </StyledScrollable>
          </StyledHoverGroup>
        )
      }
    </StyledWrapper>
  );
}
